import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api/client';
import { Loader2, ArrowLeft } from 'lucide-react';
import clsx from 'clsx';

// Step Components
import ProfileStep from './steps/ProfileStep';
import LocationStep from './steps/LocationStep';
import HeritageStep from './steps/HeritageStep';

const STEPS = [
  { id: 'profile', title: 'Your Profile', description: 'Tell us about yourself' },
  { id: 'location', title: 'Where You Live', description: 'Your current location' },
  { id: 'heritage', title: 'Your Roots', description: 'Where you\'re from in India' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // If onboarding is already complete, redirect to Identity Board
  useEffect(() => {
    if (user?.onboardingCompleted) {
      navigate('/lab/identity-board/create');
    }
  }, [user?.onboardingCompleted, navigate]);

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step — mark onboarding complete server-side, then refresh the
      // user so onboardingCompleted routes past /onboarding on future logins
      setIsLoading(true);
      try {
        await api.post('/onboarding/complete');
        await refreshUser();
        navigate('/lab/identity-board/create');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <ProfileStep onComplete={handleNext} />;
      case 1:
        return <LocationStep onComplete={handleNext} />;
      case 2:
        return <HeritageStep onComplete={handleNext} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[80vh] flex flex-col">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <span className="text-sm font-medium text-primary-600">
            {Math.round(((currentStep + 1) / STEPS.length) * 100)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={clsx(
              'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
              index === currentStep
                ? 'bg-primary-500 text-white'
                : index < currentStep
                ? 'bg-primary-100 text-primary-600'
                : 'bg-gray-100 text-gray-400'
            )}
          >
            {index + 1}
          </div>
        ))}
      </div>

      {/* Step Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-display font-bold mb-2">
          {STEPS[currentStep].title}
        </h1>
        <p className="text-gray-600">{STEPS[currentStep].description}</p>
      </div>

      {/* Step Content */}
      <div className="flex-1 max-w-xl mx-auto w-full">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-all',
            currentStep === 0
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-100'
          )}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {isLoading && (
          <div className="flex items-center gap-2 text-primary-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Saving...</span>
          </div>
        )}
      </div>
    </div>
  );
}
